"use client";

import { useLingui } from "@lingui/react/macro";
import { LocationIcon } from "@offstreetparking/design-system/icons/LocationIcon";
import { PermitTypeIcon } from "@offstreetparking/design-system/icons/PermitTypeIcon";
import { TenantIcon } from "@offstreetparking/design-system/icons/TenantIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import type { ColorRamp } from "@offstreetparking/domain/value/branding";
import { useMemo } from "react";
import { CommonLayout, FlowThemeProvider } from "../../../host";
import {
	useIsProcessingPermit,
	usePermitActiveStep,
	usePermitFormData,
	usePermitRequest,
	usePermitWizardSettings,
} from "../../CreatePermitWizard/PermitContextProvider";
import { SummaryContent } from "../../CreatePermitWizard/components/Summary/SummaryContent";
import { resolveRegistrationInstructions } from "../../CreatePermitWizard/resolveRegistrationInstructions";
import { PermitTypeHeader, type PermitTypeHeaderProps } from "../../PermitTypeHeader";
import { WysiwygView } from "../../forms/Wysiwyg/WysiwygView";
import { Contact } from "../Contact";
import { ContentCard } from "../ContentCard";
type PermitFormLayoutProps = {
	children: React.ReactNode;
	name: string;
	branding: {
		id: number;
		primaryColorRamp: ColorRamp;
		accentColorRamp: ColorRamp;
		companyLogoFileUrl?: string | null | undefined;
		faviconFileUrl?: string | null | undefined;
	} | null;
	whiteLabel?: {
		portalLogo: string;
		portalName: string;
	};
	companyName: string;
	support?: {
		instructions: string;
		hideOffstreetSupport: boolean;
	} | null;
	contact?: {
		email?: string;
		phoneNumber?: string;
		formattedPhoneNumber?: string;
	} | null;
	registrationInstructions?: string;
	eventId?: string;
	locationId?: string;
	tenantId?: number;
	location?: {
		id: number;
		name: string;
	};
	selectedTenant?: {
		name: string;
	};
	selectedPermitType?: {
		name: string;
		isEvent: boolean;
	};
	brandingUrl?: PermitTypeHeaderProps["brandingUrl"];
	venue?: PermitTypeHeaderProps["venue"];
	onChangeLocation?: () => void;
	onChangeTenant?: () => void;
	onChangePermitType?: () => void;
};
export const PermitFormLayout = ({
	children,
	branding,
	whiteLabel,
	companyName,
	support,
	contact,
	name,
	registrationInstructions,
	eventId,
	locationId,
	tenantId,
	location,
	selectedTenant,
	selectedPermitType,
	brandingUrl,
	venue,
	onChangeLocation,
	onChangeTenant,
	onChangePermitType,
}: PermitFormLayoutProps) => {
	const { t } = useLingui();
	const companyLogo = branding?.companyLogoFileUrl
		? {
				url: branding.companyLogoFileUrl,
				altText: companyName,
			}
		: whiteLabel
			? {
					url: whiteLabel.portalLogo,
					altText: whiteLabel.portalName,
				}
			: undefined;
	const activeStepId = usePermitActiveStep();
	const isProcessingPermit = useIsProcessingPermit();
	const wizardSettings = usePermitWizardSettings();
	const formData = usePermitFormData();
	const request = usePermitRequest();
	const resolvedRegistrationInstructions = useMemo(
		() =>
			resolveRegistrationInstructions({
				baselineInstructions: registrationInstructions,
				wizardSettings,
				formData,
			}),
		[registrationInstructions, wizardSettings, formData],
	);
	const isConfirmationStep = activeStepId === "confirmation" && !isProcessingPermit;
	const showPermitTypeCard = !!selectedPermitType && !selectedPermitType.isEvent;
	const hasSummaryCards = !isConfirmationStep && (!!location || !!selectedTenant || showPermitTypeCard);
	return (
		<CommonLayout
			hideParkerSetup
			srOnlyTitle={name}
			infoSlot={
				<div className="space-y-12">
					<Heading As="h1" className="break-words font-extrabold text-6xl text-primary">
						{name}
					</Heading>

					<Contact
						supportText={support?.instructions}
						hideOffstreetSupport={support?.hideOffstreetSupport ?? false}
						name={name}
						email={contact?.email}
						phoneNumber={contact?.phoneNumber}
						formattedPhoneNumber={contact?.formattedPhoneNumber}
						locationId={locationId}
						eventId={eventId}
					/>
					{!!resolvedRegistrationInstructions && (
						<div className={"flex w-full flex-col gap-3"}>
							<Heading As="h2" className="font-bold leading-6">
								{t`Parking instructions`}
							</Heading>
							<WysiwygView content={resolvedRegistrationInstructions} />
						</div>
					)}
				</div>
			}
			companyLogo={companyLogo}
			eventId={eventId}
			locationId={locationId}
			tenantId={tenantId}
		>
			<FlowThemeProvider branding={branding} />
			<ContentCard>
				{!isConfirmationStep && (
					<PermitTypeHeader
						name={request?.title ?? selectedPermitType?.name ?? name}
						brandingUrl={brandingUrl}
						venue={venue}
					/>
				)}
				{!isConfirmationStep && request && (
					<div className="border-neutral-faded border-b bg-info-faded px-4 py-3 md:px-8">
						<Text size="sm" className="text-info">
							{t`This is a permit request. Your permit is issued once the parking team approves it.`}
						</Text>
					</div>
				)}
				{hasSummaryCards && (
					<div>
						{location && (
							<div data-summary-item className="border-neutral-faded border-b px-4 py-3 md:px-8 md:py-4">
								<SummaryContent
									title={
										<span className="flex items-center gap-1.5">
											<LocationIcon className="h-4 w-4" />
											{t`Parking Locations`}
										</span>
									}
									action={
										onChangeLocation
											? {
													label: t`Details`,
													ariaLabel: `${t`Details`} ${t`Parking Locations`}`,
													onClick: onChangeLocation,
												}
											: undefined
									}
								>
									<Text size="sm">{location.name}</Text>
								</SummaryContent>
							</div>
						)}
						{selectedTenant && (
							<div data-summary-item className="border-neutral-faded border-b px-4 py-3 md:px-8 md:py-4">
								<SummaryContent
									title={
										<span className="flex items-center gap-1.5">
											<TenantIcon className="h-4 w-4" />
											{t({
												message: "Tenant",
												comment:
													"Multi-tenancy sense: a permission-bearing group under a company, not a residential renter. Keep FR locataire / ES inquilino.",
											})}
										</span>
									}
									action={
										onChangeTenant
											? {
													label: t`Change`,
													ariaLabel: t`Change Tenant`,
													onClick: onChangeTenant,
												}
											: undefined
									}
								>
									<Text size="sm">{selectedTenant.name}</Text>
								</SummaryContent>
							</div>
						)}
						{showPermitTypeCard && (
							<div data-summary-item className="border-neutral-faded border-b px-4 py-3 md:px-8 md:py-4">
								<SummaryContent
									title={
										<span className="flex items-center gap-1.5">
											<PermitTypeIcon className="h-4 w-4" />
											{t`Permit Type`}
										</span>
									}
									action={
										onChangePermitType
											? {
													label: t`Change`,
													ariaLabel: t`Change Permit Type`,
													onClick: onChangePermitType,
												}
											: undefined
									}
								>
									<Text size="sm">{selectedPermitType.name}</Text>
								</SummaryContent>
							</div>
						)}
					</div>
				)}
				<div className="h-full w-full lg:h-auto print:h-auto">{children}</div>
				<div className="border-neutral-faded border-t p-4 md:p-8 xl:hidden">
					<Contact
						supportText={support?.instructions}
						hideOffstreetSupport={support?.hideOffstreetSupport ?? false}
						name={name}
						email={contact?.email}
						phoneNumber={contact?.phoneNumber}
						formattedPhoneNumber={contact?.formattedPhoneNumber}
						locationId={locationId}
						eventId={eventId}
					/>
				</div>
			</ContentCard>
		</CommonLayout>
	);
};
